import { useState, useEffect } from 'react';
import { apiService } from '@/lib/services/api.service';
import { getUserDevice } from '@/lib/utils/device';
import {
  trackEvent,
  trackEventLegacy,
  EventAction,
  EventCategory,
} from '@/lib/utils/analytics';
import { Session } from 'next-auth';
import { logger } from '@/lib/utils/error.utils';
import { UAParser } from 'ua-parser-js';
import { API_CONFIG, APP_CONFIG } from '../config/api.config';

// Event types
export enum TrackingEventType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  QUESTION = 'QUESTION',
  FEEDBACK = 'FEEDBACK',
  PROFILE_VIEW = 'PROFILE_VIEW',
  PROFILE_EDIT = 'PROFILE_EDIT',
  PROFILE_UPDATE = 'PROFILE_UPDATE',
  CHAT_STARTED = 'CHAT_STARTED',
  CHAT_COMPLETED = 'CHAT_COMPLETED',
  PAGE_VIEW = 'PAGE_VIEW',
  FEATURE_USED = 'FEATURE_USED',
  CONVERSATION_STARTED = 'CONVERSATION_STARTED',
  CONVERSATION_ENDED = 'CONVERSATION_ENDED',
}

export enum InteractionType {
  SUGGESTED_QUESTION = 'SUGGESTED_QUESTION',
  LEGAL_SOURCE = 'LEGAL_SOURCE',
  CCQ_ARTICLE = 'CCQ_ARTICLE',
  BUTTON_CLICK = 'BUTTON_CLICK',
  LINK_CLICK = 'LINK_CLICK',
  FORM_SUBMIT = 'FORM_SUBMIT',
  PAGE_VIEW = 'PAGE_VIEW',
  CHAT_INTERACTION = 'CHAT_INTERACTION',
  THUMBS_UP = 'THUMBS_UP',
  THUMBS_DOWN = 'THUMBS_DOWN',
}

interface TrackingOptions {
  userId?: string;
  targetId?: string;
  metadata?: Record<string, any>;
  session?: Session | null;
}

interface TrackingEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  dimensions?: Record<string, string | number>;
}

interface SessionData {
  startTime: number;
  lastActivity: number;
  interactions: number;
}

// Utility function for safe browser API access
const safeExecute = (fn: () => void) => {
  try {
    fn();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      logger.log('Tracking operation failed silently:', error);
    }
  }
};

// Main tracking service
export class TrackingService {
  private session: SessionData | null = null;
  private deviceInfo: UAParser.IResult | null = null;
  private readonly activityTimeout = 30 * 60 * 1000; // 30 minutes
  private activityTimer: NodeJS.Timeout | null = null;
  private initialized = false;
  private eventListeners: Array<{
    target: EventTarget;
    type: string;
    listener: EventListener;
  }> = [];

  constructor() {
    // Empty constructor
  }

  init() {
    try {
      if (this.initialized || typeof window === 'undefined') {
        return;
      }

      this.deviceInfo = new UAParser().getResult();
      this.setupSessionTracking();
      this.initialized = true;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        logger.log('Tracking initialization failed silently:', error);
      }
    }
  }

  private addEventListenerSafely(
    target: EventTarget,
    type: string,
    listener: EventListener,
    options?: AddEventListenerOptions
  ) {
    try {
      target.addEventListener(type, listener, options);
      this.eventListeners.push({ target, type, listener });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        logger.log(`Failed to add event listener for ${type}:`, error);
      }
    }
  }

  cleanup() {
    safeExecute(() => {
      // Clean up all event listeners
      this.eventListeners.forEach(({ target, type, listener }) => {
        try {
          target.removeEventListener(type, listener);
        } catch (error) {
          // Ignore cleanup errors
        }
      });
      this.eventListeners = [];

      // Clear any timers
      if (this.activityTimer) {
        clearTimeout(this.activityTimer);
        this.activityTimer = null;
      }

      this.session = null;
      this.initialized = false;
    });
  }

  private setupSessionTracking() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Start session on first activity
    const handleFirstActivity = () => this.handleUserActivity();
    this.addEventListenerSafely(window, 'mousemove', handleFirstActivity, {
      once: true,
    });
    this.addEventListenerSafely(window, 'keydown', handleFirstActivity, {
      once: true,
    });
    this.addEventListenerSafely(window, 'click', handleFirstActivity, {
      once: true,
    });

    // Track ongoing activity
    const handleActivity = () => this.updateLastActivity();
    this.addEventListenerSafely(window, 'mousemove', handleActivity);
    this.addEventListenerSafely(window, 'keydown', handleActivity);
    this.addEventListenerSafely(window, 'click', handleActivity);

    // Handle page visibility changes
    this.addEventListenerSafely(document, 'visibilitychange', () => {
      safeExecute(() => {
        if (document.hidden) {
          this.handlePageHide();
        } else {
          this.handlePageShow();
        }
      });
    });

    // Handle page unload
    this.addEventListenerSafely(window, 'beforeunload', () =>
      this.handlePageUnload()
    );
  }

  private handleUserActivity() {
    safeExecute(() => {
      if (!this.session) {
        this.session = {
          startTime: Date.now(),
          lastActivity: Date.now(),
          interactions: 0,
        };
        this.trackEvent({
          category: 'Session',
          action: 'Start',
          dimensions: this.getDeviceInfo(),
        }).catch(() => {}); // Explicitly ignore tracking errors
      }
    });
  }

  private updateLastActivity() {
    safeExecute(() => {
      if (this.session) {
        this.session.lastActivity = Date.now();
        this.session.interactions++;

        if (this.activityTimer) {
          clearTimeout(this.activityTimer);
        }
        this.activityTimer = setTimeout(
          () => this.endSession(),
          this.activityTimeout
        );
      }
    });
  }

  private handlePageHide() {
    if (this.session) {
      const duration = Date.now() - this.session.startTime;
      this.trackEvent({
        category: 'Session',
        action: 'PageHide',
        value: duration,
      });
    }
  }

  private handlePageShow() {
    // If session expired during hide, start new session
    if (
      this.session &&
      Date.now() - this.session.lastActivity > this.activityTimeout
    ) {
      this.endSession();
      this.handleUserActivity();
    }
  }

  private handlePageUnload() {
    if (this.session) {
      const duration = Date.now() - this.session.startTime;
      this.trackEvent({
        category: 'Session',
        action: 'End',
        value: duration,
        dimensions: {
          interactions: this.session.interactions,
        },
      });
    }
  }

  private endSession() {
    if (this.session) {
      const duration = Date.now() - this.session.startTime;
      this.trackEvent({
        category: 'Session',
        action: 'End',
        value: duration,
        dimensions: {
          interactions: this.session.interactions,
        },
      });
      this.session = null;
    }
  }

  private getDeviceInfo(): Record<string, string> {
    if (!this.deviceInfo) {
      return {
        deviceType: 'unknown',
        browser: 'unknown',
        browserVersion: 'unknown',
        os: 'unknown',
        osVersion: 'unknown',
      };
    }

    return {
      deviceType: this.deviceInfo.device.type || 'desktop',
      browser: this.deviceInfo.browser.name || 'unknown',
      browserVersion: this.deviceInfo.browser.version || 'unknown',
      os: this.deviceInfo.os.name || 'unknown',
      osVersion: this.deviceInfo.os.version || 'unknown',
    };
  }

  async trackEvent(event: TrackingEvent) {
    if (!this.initialized) {
      return;
    }

    try {
      await apiService.post(API_CONFIG.ENDPOINTS.TRACKING.EVENT, {
        ...event,
        timestamp: new Date().toISOString(),
        sessionId: this.session?.startTime,
        ...this.getDeviceInfo(),
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        logger.log('Failed to track event:', { error, event });
      }
    }
  }

  /**
   * Static tracking methods with enhanced error handling
   */
  static async trackActivity(
    eventType: TrackingEventType,
    options: TrackingOptions = {}
  ) {
    try {
      const deviceInfo = getUserDevice();
      const metadata = {
        ...(options.metadata || {}),
        page:
          typeof window !== 'undefined' ? window.location.pathname : 'unknown',
        timestamp: new Date().toISOString(),
      };

      // Ensure user ID is properly tracked to associate metrics
      const userId = options.userId || 'anonymous';

      // For public pages (like login), we should still track in Google Analytics
      // but skip the API call if we know there's no session
      const isPublicPage =
        typeof window !== 'undefined' &&
        APP_CONFIG.ROUTES.OPEN_ROUTES.includes(window.location.pathname);

      // Array to hold our tracking promises
      const trackingPromises = [];

      // Only attempt API tracking if we have a session containing a valid access token,
      // or if it's not a known public page (where we might expect no session).
      // Check common locations for the access token (adjust if your session structure differs).
      const accessToken =
        options.session?.user?.accessToken ||
        (options.session as any)?.accessToken;
      const hasValidTokenInSession = !!accessToken;

      if (hasValidTokenInSession) {
        trackingPromises.push(
          apiService
            .post(
              '/tracking/activity',
              {
                eventType,
                userId,
                deviceInfo,
                metadata,
              },
              options.session
            )
            .catch((err) => {
              // Don't log warnings for expected auth errors on public pages
              if (
                !isPublicPage ||
                !err.message?.includes('No access token available')
              ) {
                logger.warn('Failed to track activity via API:', err);
              }
            })
        );
      }

      // Track in Google Analytics - this doesn't require authentication
      trackingPromises.push(
        new Promise<void>((resolve) => {
          try {
            let gaCategory: EventCategory = EventCategory.USER;
            let gaAction: EventAction = EventAction.USER_LOGIN; // Default fallback

            switch (eventType) {
              case TrackingEventType.LOGIN:
                gaAction = EventAction.USER_LOGIN;
                break;
              case TrackingEventType.LOGOUT:
                gaAction = EventAction.USER_LOGOUT;
                break;
              case TrackingEventType.QUESTION:
                gaCategory = EventCategory.QUESTION;
                gaAction = EventAction.QUESTION_ASKED;
                break;
              case TrackingEventType.FEEDBACK:
                gaCategory = EventCategory.FEEDBACK;
                gaAction = EventAction.FEEDBACK_GIVEN;
                break;
              case TrackingEventType.PAGE_VIEW:
                gaCategory = EventCategory.NAVIGATION;
                gaAction = EventAction.PAGE_VIEW;
                break;
              case TrackingEventType.CONVERSATION_STARTED:
                gaCategory = EventCategory.CONVERSATION;
                gaAction = EventAction.CONVERSATION_STARTED;
                break;
              case TrackingEventType.CONVERSATION_ENDED:
                gaCategory = EventCategory.CONVERSATION;
                gaAction = EventAction.CONVERSATION_ENDED;
                break;
            }

            trackEvent({
              category: gaCategory,
              action: gaAction,
              label: options.targetId || metadata.page,
              value: 1,
              nonInteraction: false,
            });
            resolve();
          } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
              logger.log('Failed to track GA event:', error);
            }
            resolve();
          }
        })
      );

      // Wait for all tracking to complete
      await Promise.all(trackingPromises);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        logger.log('Failed to track activity:', error);
      }
    }
  }

  /**
   * Track user interaction with UI elements
   */
  static async trackInteraction(
    interactionType: InteractionType,
    targetId: string,
    options: TrackingOptions = {}
  ) {
    try {
      const deviceInfo = getUserDevice();

      // For public pages (like login), we should still track in Google Analytics
      // but skip the API call if we know there's no session
      const isPublicPage =
        typeof window !== 'undefined' &&
        APP_CONFIG.ROUTES.OPEN_ROUTES.includes(window.location.pathname);

      // Array to hold our tracking promises
      const trackingPromises = [];

      // Only attempt API tracking if we have a session or it's not a known public page
      if (options.session || !isPublicPage) {
        trackingPromises.push(
          apiService
            .post(
              '/tracking/interaction',
              {
                eventType: interactionType,
                targetId,
                userId: options.userId,
                deviceInfo,
                metadata: options.metadata,
              },
              options.session
            )
            .catch((err) => {
              // Don't log warnings for expected auth errors on public pages
              if (
                !isPublicPage ||
                !err.message?.includes('No access token available')
              ) {
                logger.warn('Failed to track interaction via API:', err);
              }
            })
        );
      }

      // Track in Google Analytics - this doesn't require authentication
      trackingPromises.push(
        new Promise<void>((resolve) => {
          try {
            let gaAction: EventAction;

            switch (interactionType) {
              case InteractionType.SUGGESTED_QUESTION:
                gaAction = EventAction.SUGGESTED_QUESTION_CLICK;
                break;
              case InteractionType.LEGAL_SOURCE:
                gaAction = EventAction.LEGAL_SOURCE_CLICK;
                break;
              case InteractionType.CCQ_ARTICLE:
                gaAction = EventAction.CCQ_ARTICLE_CLICK;
                break;
              case InteractionType.BUTTON_CLICK:
                gaAction = EventAction.BUTTON_CLICK;
                break;
              case InteractionType.LINK_CLICK:
                gaAction = EventAction.LINK_CLICK;
                break;
              case InteractionType.FORM_SUBMIT:
                gaAction = EventAction.FORM_SUBMIT;
                break;
              default:
                gaAction = EventAction.BUTTON_CLICK;
            }

            trackEvent({
              category: EventCategory.INTERACTION,
              action: gaAction,
              label: targetId,
              value: 1,
              nonInteraction: false,
            });
            resolve();
          } catch (error) {
            resolve();
          }
        })
      );

      // Wait for all tracking to complete
      await Promise.all(trackingPromises);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        logger.log('Failed to track interaction:', error);
      }
    }
  }

  /**
   * Track message analytics
   */
  static async trackMessage(
    messageId: string,
    responseTime: number,
    totalTime: number,
    options: TrackingOptions = {}
  ) {
    try {
      await apiService.post(
        '/tracking/message',
        {
          messageId,
          responseTime,
          totalTime,
          topic: options.metadata?.topic,
          userId: options.userId,
        },
        options.session
      );

      // Also track in Google Analytics
      trackEvent({
        category: EventCategory.PERFORMANCE,
        action: EventAction.MESSAGE_RESPONSE,
        label: options.metadata?.topic || 'response_time',
        value: responseTime,
      });
    } catch (error) {
      logger.error('Failed to track message:', error);
    }
  }

  /**
   * Track user feedback
   */
  static async trackFeedback(
    isPositive: boolean,
    messageId: string,
    options: TrackingOptions = {}
  ) {
    try {
      const eventAction = isPositive
        ? EventAction.FEEDBACK_POSITIVE
        : EventAction.FEEDBACK_NEGATIVE;

      await trackEvent({
        category: EventCategory.FEEDBACK,
        action: eventAction,
        label: messageId,
        value: 1,
      });
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Track response time metrics
   */
  static async trackResponseTime(
    duration: number,
    options: TrackingOptions = {}
  ) {
    try {
      await Promise.all([
        apiService
          .post(
            '/tracking/event',
            {
              category: 'Performance',
              action: 'ResponseTime',
              value: duration,
              dimensions: {
                userId: options.userId,
              },
            },
            options.session
          )
          .catch(() => {}),

        // Track in Google Analytics
        new Promise<void>((resolve) => {
          try {
            trackEvent({
              category: EventCategory.PERFORMANCE,
              action: EventAction.MESSAGE_RESPONSE,
              value: Math.round(duration / 100) * 100, // Round to nearest 100ms
              nonInteraction: true,
            });
            resolve();
          } catch (error) {
            resolve();
          }
        }),
      ]);
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Track page views
   */
  static async trackPageView(path: string, options: TrackingOptions = {}) {
    try {
      await Promise.all([
        TrackingService.trackActivity(TrackingEventType.PAGE_VIEW, {
          ...options,
          metadata: {
            ...(options.metadata || {}),
            path,
          },
        }),

        // Track in Google Analytics directly for page view events
        new Promise<void>((resolve) => {
          try {
            if (typeof window !== 'undefined' && (window as any).gtag) {
              (window as any).gtag(
                'config',
                process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
                {
                  page_path: path,
                }
              );
            }
            resolve();
          } catch (error) {
            resolve();
          }
        }),
      ]);
    } catch (error) {
      // Silently fail tracking attempts
    }
  }

  // User Activity Tracking
  trackUserAction(action: string, details?: Record<string, any>) {
    this.trackEvent({
      category: 'UserAction',
      action,
      dimensions: details,
    });
  }

  // Question Tracking
  trackQuestion(question: string, category: string) {
    this.trackEvent({
      category: 'Question',
      action: 'Asked',
      label: category,
      dimensions: {
        question,
      },
    });
  }

  trackResponse(questionId: string, responseTime: number, successful: boolean) {
    this.trackEvent({
      category: 'Response',
      action: successful ? 'Success' : 'Failure',
      value: responseTime,
      dimensions: {
        questionId,
      },
    });
  }

  // Feedback Tracking
  trackFeedback(questionId: string, rating: number, comments?: string) {
    this.trackEvent({
      category: 'Feedback',
      action: 'Submit',
      value: rating,
      dimensions: {
        questionId,
        ...(comments && { comments }),
      },
    });
  }

  // Error Tracking
  trackError(error: Error, context?: Record<string, any>) {
    this.trackEvent({
      category: 'Error',
      action: error.name,
      label: error.message,
      dimensions: context,
    });
  }

  // Performance Tracking
  trackPerformance(
    metric: string,
    value: number,
    context?: Record<string, any>
  ) {
    this.trackEvent({
      category: 'Performance',
      action: metric,
      value,
      dimensions: context,
    });
  }
}

export const trackingService = new TrackingService();

// Hook for automatic activity tracking on page load/unload
export function useActivityTracking(userId?: string, session?: Session | null) {
  const [sessionId] = useState(Math.random().toString(36).substring(2, 15));

  useEffect(() => {
    try {
      // Initialize tracking service
      trackingService.init();

      // Track login/page view
      TrackingService.trackActivity(TrackingEventType.LOGIN, {
        userId,
        session,
        metadata: {
          sessionId,
          url: typeof window !== 'undefined' ? window.location.pathname : '',
        },
      }).catch(() => {}); // Explicitly ignore tracking errors

      // Track logout on page unload
      const handleUnload = () => {
        safeExecute(() => {
          TrackingService.trackActivity(TrackingEventType.LOGOUT, {
            userId,
            session,
            metadata: {
              sessionId,
              duration:
                typeof performance !== 'undefined'
                  ? Date.now() - performance.now()
                  : 0,
            },
          }).catch(() => {});
        });
      };

      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', handleUnload);
      }

      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('beforeunload', handleUnload);
        }
        trackingService.cleanup();
      };
    } catch (error) {
      // Silently fail in production
      if (process.env.NODE_ENV !== 'production') {
        logger.log('Activity tracking setup failed:', error);
      }
      return () => {};
    }
  }, [userId, sessionId, session]);
}
